import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getDropdown } from "../../Redux/Actions/UsersProfileAction";
import { toast } from "react-toastify";
import { Autocomplete, TextField } from "@mui/material";
import Box from "@mui/joy/Box";
import Slider, { sliderClasses } from "@mui/joy/Slider";

const FilterForm = ({ formData, handleFormChange, handleSubmit, resetForm }) => {
  const dispatch = useDispatch();
  const [castData, setCastData] = useState([]);
  const [maritalData, setMaritalData] = useState([]);

  // Load saved filters from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem("userFilters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      Object.keys(parsedFilters).forEach((key) => {
        if (parsedFilters[key] !== null && parsedFilters[key] !== "") {
          handleFormChange({
            target: { name: key, value: parsedFilters[key] },
          });
        }
      });
    }
  }, []);

  // Save filters to localStorage on change
  useEffect(() => {
    localStorage.setItem("userFilters", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const fetchDropDown = async () => {
      try {
        const res = await dispatch(getDropdown());
        if (res?.data?.data?.cast) {
          setCastData(res.data.data.cast);
          setMaritalData(res.data.data.marital);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch data.");
      }
    };
    fetchDropDown();
  }, [dispatch]);

  const castOptions = useMemo(
    () => castData.map((item) => ({ label: item.text, value: item.text })),
    [castData]
  );

  const maritalOptions = useMemo(
    () => maritalData.map((item) => ({ label: item.text, value: item.text })),
    [maritalData]
  );

  const isFormEmpty = useMemo(
    () => Object.values(formData).every((value) => !value || value === ""),
    [formData]
  );

  const handleResetForm = () => {
    localStorage.removeItem("userFilters");
    resetForm();
  };

  // Function to convert inches to feet and inches format
  const formatHeight = (inches) => {
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  // Regular Range Slider (Age, Weight & Height)
  const RangeSlider = ({ label, minName, maxName, min, max, step, unit, formatValue }) => {
    const minValue = parseFloat(formData[minName]) || min;
    const maxValue = parseFloat(formData[maxName]) || max;

    const [localValue, setLocalValue] = useState([minValue, maxValue]);

    useEffect(() => {
      setLocalValue([minValue, maxValue]);
    }, [minValue, maxValue]);

    const handleSliderChange = (event, newValue) => {
      setLocalValue(newValue);
    };

    const handleChangeCommitted = (event, newValue) => {
      handleFormChange({
        target: { name: minName, value: newValue[0].toString() },
      });
      handleFormChange({
        target: { name: maxName, value: newValue[1].toString() },
      });
    };

    const displayValue = formatValue
      ? `${formatValue(localValue[0])} - ${formatValue(localValue[1])}`
      : `${localValue[0]} - ${localValue[1]} ${unit}`;

    return (
      <div>
        <label className="block text-md font-medium text-gray-700 mb-2">
          {label}:{" "}
          <span className="text-[#E72848] font-semibold">
            {displayValue}
          </span>
        </label>

        <Box sx={{ width: "100%", px: 1 }}>
          <Slider
            value={localValue}
            onChange={handleSliderChange}
            onChangeCommitted={handleChangeCommitted}
            min={min}
            max={max}
            step={step}
            getAriaLabel={() => `${label} range`}
            marks={[
              {
                value: min,
                label: formatValue ? formatValue(min) : `${min}${unit === "years" ? "" : unit}`
              },
              {
                value: max,
                label: formatValue ? formatValue(max) : `${max}${unit === "years" ? "" : unit}`
              },
            ]}
            sx={{
              color: "#E72848",
              height: 6,
              padding: "16px 0",
              [`& .${sliderClasses.rail}`]: {
                backgroundColor: "#e5e7eb",
                opacity: 1,
              },
              [`& .${sliderClasses.track}`]: {
                backgroundColor: "#E72848",
                border: "none",
                height: 6,
              },
              [`& .${sliderClasses.thumb}`]: {
                width: 18,
                height: 18,
                backgroundColor: "#E72848",
                border: "none",
                transition: "all 0.2s ease",
                boxShadow: "0 0 0 3px rgba(231, 40, 72, 0.25)",
                "&:hover": {
                  backgroundColor: "#d62040",
                  boxShadow: "0 0 0 5px rgba(231, 40, 72, 0.2)",
                },
                "&::before": {
                  boxShadow: "none !important",
                  border: "none !important",
                  background: "transparent !important",
                },
                "&:focus, &:focus-visible, &.Mui-focusVisible": {
                  outline: "none",
                  border: "none",
                  boxShadow: "0 0 0 3px rgba(231, 40, 72, 0.25)",
                },
                "&:active": {
                  boxShadow: "0 0 0 4px rgba(231, 40, 72, 0.3)",
                },
              },
              [`& .${sliderClasses.mark}`]: {
                backgroundColor: "#E72848",
                width: 5,
                height: 5,
                borderRadius: "50%",
              },
              [`& .${sliderClasses.markLabel}`]: {
                color: "#6b7280",
                fontSize: "0.75rem",
                fontWeight: 400,
              },
            }}
          />
        </Box>
      </div>
    );
  };

  // Autocomplete Field Component
  const AutoCompleteField = ({
    label,
    name,
    options,
    placeholder,
    freeSolo = false,
  }) => {
    if (freeSolo) {
      return (
        <div>
          <label className="block text-md font-medium text-gray-700 mb-1">
            {label}
          </label>
          <TextField
            size="small"
            fullWidth
            value={formData[name] || ""}
            onChange={(e) =>
              handleFormChange({
                target: { name, value: e.target.value },
              })
            }
            placeholder={placeholder}
            variant="outlined"
            sx={{
              ".MuiInputBase-root": {
                background: "#fff !important",
                height: "45px",
                borderRadius: "12px",
              },
              ".MuiOutlinedInput-root": {
                height: "45px",
                borderRadius: "12px",
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: "1px solid #d1d5db",
              },
              ".MuiInputBase-input": {
                padding: "0 14px",
                fontSize: "16px",
                height: "100%",
                boxSizing: "border-box",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B3622D",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B3622D",
                borderWidth: "1px",
              },
            }}
          />
        </div>
      );
    }

    return (
      <div>
        <label className="block text-md font-medium text-gray-700 mb-1">
          {label}
        </label>
        <Autocomplete
          size="small"
          fullWidth
          options={options || []}
          getOptionLabel={(option) => option.label || ""}
          value={options?.find((opt) => opt.value === formData[name]) || null}
          onChange={(event, newValue) => {
            handleFormChange({
              target: { name, value: newValue?.value || "" },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              sx={{
                ".MuiInputBase-root": {
                  background: "#fff !important",
                  height: "45px",
                  borderRadius: "12px",
                },
                ".MuiOutlinedInput-root": {
                  height: "45px",
                  borderRadius: "12px",
                },
                ".MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #d1d5db",
                },
                ".MuiInputBase-input": {
                  padding: "0 14px",
                  fontSize: "16px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B3622D",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#B3622D",
                },
              }}
            />
          )}
        />
      </div>
    );
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem("userFilters", JSON.stringify(formData));
    handleSubmit(e);
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      className="bg-[#F5F5F5] shadow-md rounded-xl p-4 space-y-4"
    >
      {/* Looking For */}
      <AutoCompleteField
        label="I am looking for"
        name="gender"
        options={[
          { label: "Man", value: "Male" },
          { label: "Woman", value: "Female" },
        ]}
        placeholder="Select gender"
      />

      {/* Age Range */}
      <RangeSlider
        label="Age Range"
        minName="min_age"
        maxName="max_age"
        min={18}
        max={70}
        step={1}
        unit="years"
      />

      {/* Height Range in Inches */}
      <RangeSlider
        label="Height Range"
        minName="min_height"
        maxName="max_height"
        min={48}  // 4'7" in inches
        max={96}  // 6'6" in inches
        step={1}
        unit="inches"
        formatValue={formatHeight}
      />

      {/* Weight Range */}
      <RangeSlider
        label="Weight Range"
        minName="min_weight"
        maxName="max_weight"
        min={30}
        max={150}
        step={1}
        unit="kg"
      />

      {/* Cast */}
      <AutoCompleteField
        label="Cast"
        name="cast"
        options={castOptions}
        placeholder="Select Cast"
      />

      {/* Marital Status */}
      <AutoCompleteField
        label="Marital Status"
        name="marital_status"
        options={maritalOptions}
        placeholder="Select Marital Status"
      />

      {/* Education */}
      <div>
        <label className="block text-md font-medium text-gray-700 mb-1">
          Education
        </label>

        <TextField
          size="small"
          fullWidth
          value={formData.education || ""}
          onChange={(e) =>
            handleFormChange({
              target: { name: "education", value: e.target.value },
            })
          }
          placeholder="Enter education"
          variant="outlined"
          sx={{
            ".MuiInputBase-root": {
              background: "#fff !important",
              height: "45px",
              borderRadius: "12px !important", // ⛔ No border radius
            },
            ".MuiOutlinedInput-root": {
              height: "45px",
              borderRadius: "12px !important", // ⛔ No border radius
            },
            ".MuiOutlinedInput-notchedOutline": {
              border: "1px solid #d1d5db",
              borderRadius: "12px !important", // ⛔ No border radius
            },
            ".MuiInputBase-input": {
              padding: "0 14px",
              fontSize: "16px",
              height: "100%",
              boxSizing: "border-box",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B3622D",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#B3622D",
              borderWidth: "1px",
            },
          }}
        />
      </div>


      {/* Buttons */}
      <button
        type="submit"
        className="w-full bg-[#E72848] text-white py-2 rounded-xl transition-colors hover:bg-[#d62040]"
      >
        Apply
      </button>
      <button
        type="button"
        onClick={handleResetForm}
        disabled={isFormEmpty}
        className={`w-full font-semibold py-2 rounded transition-colors ${isFormEmpty
          ? "text-gray-400 cursor-not-allowed"
          : "text-[#E72848] hover:text-red-700"
          }`}
      >
        Clear
      </button>
    </form>
  );
};

export default FilterForm;